import { useParams, Link, useNavigate } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useToast } from '../hooks/useToast';
import { getContentId } from '../types';
import SectionToggle from '../components/SectionToggle';
import '../styles/editor.css';

export default function PostList() {
  const { siteId } = useParams<{ siteId: string }>();
  const { items, loading, error, remove } = useContent(siteId, 'post');
  const { addToast } = useToast();
  const navigate = useNavigate();

  const handleDelete = async (contentKey: string, title: string) => {
    if (!confirm(`Delete "${title}"?`)) return;
    try {
      const id = getContentId(contentKey);
      await remove(id);
      addToast('success', 'Post deleted');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Blog</h1></div>
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header"><h1>Blog</h1></div>
        <div className="error-state"><p>{error}</p></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Blog</h1>
        <div className="page-header-actions">
          <SectionToggle siteId={siteId!} field="showBlog" label="Blog" />
          <Link to={`/sites/${siteId}/content/blog/new`} className="btn btn-primary">
            + New Post
          </Link>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ccc' }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></div>
          <h2>No blog posts yet</h2>
          <p>Share news, updates, or stories with your audience.</p>
          <p style={{ fontSize: '0.85rem', color: 'var(--color-gray-500)', marginBottom: 16 }}>
            Posts appear on your site with a title, date, and featured image.
          </p>
          <div className="empty-state-preview">
            <div className="empty-state-preview-post">
              <div className="empty-state-preview-post-image" />
              <div className="empty-state-preview-post-lines">
                <div className="empty-state-preview-line" />
                <div className="empty-state-preview-line short" />
              </div>
            </div>
            <div className="empty-state-preview-post">
              <div className="empty-state-preview-post-image" />
              <div className="empty-state-preview-post-lines">
                <div className="empty-state-preview-line" />
                <div className="empty-state-preview-line short" />
              </div>
            </div>
            <div className="empty-state-preview-post">
              <div className="empty-state-preview-post-image" />
              <div className="empty-state-preview-post-lines">
                <div className="empty-state-preview-line" />
                <div className="empty-state-preview-line short" />
              </div>
            </div>
          </div>
          <Link to={`/sites/${siteId}/content/blog/new`} className="btn btn-primary">
            + Write your first post
          </Link>
        </div>
      ) : (
        <div className="list-items">
          {items.map((item) => {
            const data = item.data as Record<string, string | boolean>;
            const contentId = getContentId(item.contentKey);
            return (
              <div key={item.contentKey} className="list-item">
                <div
                  className="list-item-content"
                  onClick={() =>
                    navigate(`/sites/${siteId}/content/blog/${contentId}`)
                  }
                >
                  <h3 className="list-item-title">{String(data.title || 'Untitled')}</h3>
                  <div className="list-item-meta">
                    {data.published ? (
                      <span className="tag tag-accent">Published</span>
                    ) : (
                      <span className="tag">Draft</span>
                    )}
                    {data.publishedAt && (
                      <span className="list-item-price">{String(data.publishedAt)}</span>
                    )}
                  </div>
                </div>
                <div className="list-item-actions">
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(item.contentKey, String(data.title || 'this post'))}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
